import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import PropertyCard from "../components/PropertyCard";
import {
  User,
  Heart,
  MessageSquare,
  Settings,
  LogOut,
  Home,
  Calendar,
  Clock,
  Phone,
  Mail,
  ArrowRight,
  ChevronRight,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const DashboardPage = () => {
  const { user, logout, token } = useAuth();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get("tab") || "overview";

  const [savedProperties, setSavedProperties] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [savedRes, inquiriesRes] = await Promise.all([
          axios.get(`${API}/saved-properties`, {
            withCredentials: true,
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }),
          axios.get(`${API}/inquiries`, {
            withCredentials: true,
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }),
        ]);
        setSavedProperties(savedRes.data);
        setInquiries(inquiriesRes.data);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const getInquiryTypeLabel = (type) => {
    switch (type) {
      case "site_visit":
        return "Site Visit";
      case "video_tour":
        return "Video Tour";
      case "callback":
        return "Callback";
      case "offer":
        return "Offer Request";
      default:
        return type;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "bg-amber-100 text-amber-700";
      case "contacted":
        return "bg-blue-100 text-blue-700";
      case "completed":
        return "bg-emerald-100 text-emerald-700";
      default:
        return "bg-stone-100 text-stone-700";
    }
  };

  return (
    <div className="min-h-screen pt-20 bg-stone-50" data-testid="dashboard-page">
      {/* Header */}
      <div className="bg-white border-b border-stone-100">
        <div className="section-container py-8">
          <div className="flex items-center gap-6">
            {user?.picture ? (
              <img src={user.picture} alt={user?.name} className="w-20 h-20 rounded-full object-cover" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-emerald-900 text-white flex items-center justify-center text-3xl font-display">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <h1 className="font-display text-2xl md:text-3xl text-stone-900">Welcome, {user?.name}</h1>
              <p className="text-stone-500 flex items-center gap-1">
                <Mail className="w-4 h-4" strokeWidth={1.5} />
                {user?.email}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="section-container py-8">
        <Tabs defaultValue={defaultTab} className="space-y-6">
          <TabsList className="bg-white border border-stone-100 p-1 rounded-sm">
            <TabsTrigger
              value="overview"
              className="data-[state=active]:bg-emerald-900 data-[state=active]:text-white rounded-sm px-6"
            >
              Overview
            </TabsTrigger>
            <TabsTrigger
              value="saved"
              className="data-[state=active]:bg-emerald-900 data-[state=active]:text-white rounded-sm px-6"
            >
              Saved Properties
            </TabsTrigger>
            <TabsTrigger
              value="inquiries"
              className="data-[state=active]:bg-emerald-900 data-[state=active]:text-white rounded-sm px-6"
            >
              My Inquiries
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-sm shadow-layered">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-emerald-50 rounded-sm flex items-center justify-center">
                    <Heart className="w-6 h-6 text-emerald-700" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="font-mono text-2xl text-stone-900">{savedProperties.length}</p>
                    <p className="text-sm text-stone-500">Saved Properties</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-sm shadow-layered">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-amber-50 rounded-sm flex items-center justify-center">
                    <MessageSquare className="w-6 h-6 text-amber-700" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="font-mono text-2xl text-stone-900">{inquiries.length}</p>
                    <p className="text-sm text-stone-500">Total Inquiries</p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-sm shadow-layered">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-50 rounded-sm flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-blue-700" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="font-mono text-2xl text-stone-900">
                      {inquiries.filter((i) => i.status === "pending").length}
                    </p>
                    <p className="text-sm text-stone-500">Pending Visits</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Saved */}
            <div className="bg-white p-6 rounded-sm shadow-layered">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-lg text-stone-900">Recently Saved</h2>
                <Link to="/dashboard?tab=saved" className="text-sm text-emerald-700 hover:text-emerald-900 flex items-center gap-1">
                  View All <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
                </Link>
              </div>

              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="bg-stone-100 animate-pulse aspect-[4/3] rounded-sm" />
                  ))}
                </div>
              ) : savedProperties.length === 0 ? (
                <div className="text-center py-8">
                  <Heart className="w-12 h-12 text-stone-300 mx-auto mb-3" strokeWidth={1} />
                  <p className="text-stone-500 mb-4">No saved properties yet</p>
                  <Link to="/search" className="btn-secondary inline-flex items-center gap-2">
                    Browse Properties <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                  </Link>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {savedProperties.slice(0, 3).map((property) => (
                    <PropertyCard key={property.property_id} property={property} />
                  ))}
                </div>
              )}
            </div>

            {/* Recent Inquiries */}
            <div className="bg-white p-6 rounded-sm shadow-layered">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-lg text-stone-900">Recent Inquiries</h2>
                <Link to="/dashboard?tab=inquiries" className="text-sm text-emerald-700 hover:text-emerald-900 flex items-center gap-1">
                  View All <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
                </Link>
              </div>

              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="bg-stone-100 animate-pulse h-16 rounded-sm" />
                  ))}
                </div>
              ) : inquiries.length === 0 ? (
                <div className="text-center py-8">
                  <MessageSquare className="w-12 h-12 text-stone-300 mx-auto mb-3" strokeWidth={1} />
                  <p className="text-stone-500 mb-4">No inquiries yet</p>
                  <Link to="/search" className="btn-secondary inline-flex items-center gap-2">
                    Explore Properties <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {inquiries.slice(0, 5).map((inquiry) => (
                    <div
                      key={inquiry.inquiry_id}
                      className="flex items-center justify-between p-4 border border-stone-100 rounded-sm hover:bg-stone-50 transition-colors"
                    >
                      <div>
                        <p className="font-medium text-stone-900">{inquiry.property_name}</p>
                        <p className="text-sm text-stone-500 flex items-center gap-2">
                          <span className="status-badge">{getInquiryTypeLabel(inquiry.inquiry_type)}</span>
                          <span>•</span>
                          <span>{new Date(inquiry.created_at).toLocaleDateString()}</span>
                        </p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-sm uppercase tracking-wide ${getStatusColor(inquiry.status)}`}>
                        {inquiry.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Saved Properties Tab */}
          <TabsContent value="saved">
            <div className="bg-white p-6 rounded-sm shadow-layered">
              <h2 className="font-display text-xl text-stone-900 mb-6">Saved Properties</h2>

              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="bg-stone-100 animate-pulse aspect-[4/3] rounded-sm" />
                  ))}
                </div>
              ) : savedProperties.length === 0 ? (
                <div className="text-center py-12">
                  <Heart className="w-16 h-16 text-stone-300 mx-auto mb-4" strokeWidth={1} />
                  <h3 className="font-display text-xl text-stone-900 mb-2">No Saved Properties</h3>
                  <p className="text-stone-500 mb-6">
                    Start saving properties you like to view them later
                  </p>
                  <Link to="/search" className="btn-primary inline-flex items-center gap-2">
                    Browse Properties <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                  </Link>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {savedProperties.map((property) => (
                    <PropertyCard key={property.property_id} property={property} />
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Inquiries Tab */}
          <TabsContent value="inquiries">
            <div className="bg-white p-6 rounded-sm shadow-layered">
              <h2 className="font-display text-xl text-stone-900 mb-6">My Inquiries</h2>

              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="bg-stone-100 animate-pulse h-20 rounded-sm" />
                  ))}
                </div>
              ) : inquiries.length === 0 ? (
                <div className="text-center py-12">
                  <MessageSquare className="w-16 h-16 text-stone-300 mx-auto mb-4" strokeWidth={1} />
                  <h3 className="font-display text-xl text-stone-900 mb-2">No Inquiries Yet</h3>
                  <p className="text-stone-500 mb-6">
                    When you request site visits or callbacks, they'll appear here
                  </p>
                  <Link to="/search" className="btn-primary inline-flex items-center gap-2">
                    Explore Properties <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {inquiries.map((inquiry) => (
                    <div
                      key={inquiry.inquiry_id}
                      className="p-4 border border-stone-100 rounded-sm hover:shadow-layered transition-shadow"
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div>
                          <h3 className="font-medium text-stone-900 mb-1">{inquiry.property_name}</h3>
                          <div className="flex flex-wrap items-center gap-3 text-sm text-stone-500">
                            <span className="status-badge">{getInquiryTypeLabel(inquiry.inquiry_type)}</span>
                            {inquiry.preferred_date && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" strokeWidth={1.5} />
                                {inquiry.preferred_date}
                              </span>
                            )}
                            {inquiry.preferred_time && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" strokeWidth={1.5} />
                                {inquiry.preferred_time}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-stone-400">
                            {new Date(inquiry.created_at).toLocaleDateString()}
                          </span>
                          <span className={`text-xs px-3 py-1 rounded-sm uppercase tracking-wide font-medium ${getStatusColor(inquiry.status)}`}>
                            {inquiry.status}
                          </span>
                        </div>
                      </div>
                      {inquiry.message && (
                        <p className="mt-3 text-sm text-stone-500 border-t border-stone-100 pt-3">
                          "{inquiry.message}"
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default DashboardPage;
